import { useRef, useState } from 'react';
import useOnClickOutside from '~/hooks/on-click-outside';
import { Translate } from '~/i18n';
import { EarthIcon } from '~/svgs/home-page';
import { ArrowDownIcon } from '~/svgs/common';
import { AnimateSharedLayout, motion } from 'framer-motion';
import { CitiesDropdownProps } from '.';
import styles from './styles.module.css';
import { useEffect } from 'react';
import Image from 'next/image';

const CitiesDropdown = ({ onChange, defaultValue, regions = [] }: CitiesDropdownProps) => {
   const [open, setOpen] = useState(false);
   const menuRef = useRef<HTMLDivElement>(null);
   const itemsRef = useRef<HTMLUListElement>(null);
   const btnRef = useRef<HTMLButtonElement>(null);

   const [selectedLabel, setLabel] = useState<any>(
      regions.find(city => city.slug === defaultValue)
   );

   useEffect(() => {
      setLabel(regions.find(city => city.slug === defaultValue));
   }, [regions, defaultValue]);

   useOnClickOutside(menuRef, () => setOpen(false));
   const onClick = () => {
      setOpen(prev => !prev);
   };

   const close = () => {
      setOpen(false);
      btnRef.current?.focus();
   };

   const preventArrowsFromScrolling = () => {
      window.addEventListener(
         'keydown',
         e => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
               e.preventDefault();
            }
         },
         { once: true }
      );
   };

   /**
    * This is responsible to handle "down arrow on keyboard" and "up arrow on keyboard"
    * to naviagte the items and focus on the correct one
    *  */
   const itemKeyUp = (e: React.KeyboardEvent<HTMLLIElement>, href: string, idx: number) => {
      const target = e.target as HTMLLIElement;
      const previous = target.previousSibling as HTMLLIElement;
      const next = target.nextSibling as HTMLLIElement;
      // If the key is space or enter, select this item

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') preventArrowsFromScrolling();

      if (e.key.trim() === '' || e.key === 'Enter') itemSelected(target, href, idx);
      else if (e.key === 'ArrowDown') next?.focus();
      else if (e.key === 'ArrowUp') {
         if (previous) previous.focus();
         else close();
      }
   };

   const buttonKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const menu = itemsRef.current;
      const first = menu?.firstChild as HTMLElement;
      e.preventDefault();
      e.preventDefault();
      if (first && e.key === 'ArrowDown') {
         setOpen(true);
         first.focus();
         preventArrowsFromScrolling();
      }
   };

   const butonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const menu = itemsRef.current;
      const first = menu?.firstChild as HTMLElement;
      e.preventDefault();
      if (first && e.key === 'Tab' && open) {
         preventArrowsFromScrolling();
         first.focus();
      }
   };

   // close the menu, store the item and tell the parent about it
   const itemSelected = (target: HTMLLIElement, href: string, idx: number) => {
      close();
      // setSelected(target);
      setLabel(regions[idx]);
      onChange(href, target);
   };

   return (
      <AnimateSharedLayout>
         <div ref={menuRef} className="relative z-50">
            <div>
               <span className="shadow-sm rounded-cornered focus:shadow-xl">
                  <button
                     ref={btnRef}
                     type="button"
                     onClick={onClick}
                     onKeyUp={buttonKeyUp}
                     onKeyDown={butonKeyDown}
                     className={styles['btn']}
                     id="options-menu"
                     aria-haspopup="true"
                     aria-expanded={open ? 'true' : 'false'}>
                     {selectedLabel && (
                        <div className="flex items-center flex-grow">
                           <Image
                              // layout="fixed"
                              className="object-cover w-10 h-10 rounded-full"
                              src={selectedLabel.background_image || '/images/not_found.svg'}
                              alt="halayalla"
                              width={40}
                              height={40}
                           />
                           <span className="text-sm ltr:ml-4 rtl:mr-4">{selectedLabel.name}</span>
                        </div>
                     )}

                     {!selectedLabel && (
                        <div className="flex items-center flex-grow">
                           <div>
                              <EarthIcon className="h-10" />
                           </div>
                           <span className="text-sm ltr:ml-4 rtl:mr-4">
                              <Translate id="common:anywhere" />
                           </span>
                        </div>
                     )}
                     <div className="float-right">
                        <ArrowDownIcon className="w-[14px] h-[8px]" />
                     </div>
                  </button>
               </span>
            </div>

            <motion.div
               layout={false}
               initial={{ height: 0 }}
               transition={{
                  duration: !open ? 0.15 : 0.2,
                  ease: 'easeInOut',
               }}
               animate={{
                  height: !open ? 0 : 'auto',
               }}
               className="absolute right-0 w-full overflow-hidden origin-top-right shadow-lg rounded-b-cornered">
               <ul
                  ref={itemsRef}
                  className="relative overflow-y-auto list-none bg-white shadow-xs text-primary-color"
                  role="menu"
                  style={{ maxHeight: 450 }}
                  aria-orientation="vertical"
                  aria-labelledby="options-menu">
                  {regions.map(
                     ({ id, background_image, slug, name }, idx) =>
                        slug !== selectedLabel?.slug && (
                           <li
                              onKeyUp={e => itemKeyUp(e, slug, idx)}
                              className={styles['list-item']}
                              key={id}
                              role="menuitem"
                              onClick={e => itemSelected(e.target as HTMLLIElement, slug, idx)}
                              tabIndex={-1}>
                              <div className="flex items-center py-1 text-xs whitespace-no-wrap pointer-events-none">
                                 <Image
                                    key={slug}
                                    // layout="fixed"
                                    className="object-cover w-10 h-10 rounded-full"
                                    src={background_image || '/images/not_found.svg'}
                                    alt="halayalla"
                                    width={40}
                                    height={40}
                                 />
                                 <span className="mx-6 pointer-events-none">{name}</span>
                              </div>
                           </li>
                        )
                  )}
               </ul>
            </motion.div>
         </div>
      </AnimateSharedLayout>
   );
};

export default CitiesDropdown;
